import { useState } from "react";
import { Link } from "react-router-dom";
import type { PlanPart, TreatmentPlan } from "@dentacore/shared-types";

import { useBranchTimezone } from "../api/use-branch-timezone";
import { useCan } from "../auth/use-can";
import { completeVisit } from "../api/plans";
import { dayOf, doneAhead, nameOf, standingOf, whenOf } from "../plans/standing";
import { Pager, useFitPages, type Follow } from "../ui/paging";
import { formatMoney } from "./money";
import styles from "./treatment-panel.module.css";
import { useBranchCurrency } from "../api/use-branch-currency";
import { CorrectVisitDialog } from "./correct-visit-dialog";

/**
 * A patient's treatment, read-only — STORY-149, decisions PV3 and PV6 — and,
 * since 2026-09-27, **a tab of its own** (PF5) rather than a list under the
 * chart.
 *
 * **Two kinds of row, both read here and edited nowhere.** A *procedure* is a
 * course booked from a multi-visit procedure (PV3): its visits come from the
 * procedure and it moves on as appointments are booked and completed. A
 * *treatment plan* is one built by hand before 2026-09-24, kept as history
 * (PV6). Neither has an editor any more, so this says where each stands and
 * offers the one action that is still the front desk's: booking the next visit,
 * which opens the booking form on it (PV5).
 *
 * **Closed by default, open while in progress** (PF8). A finished treatment is
 * history and reads as one line until somebody asks for its visits; every
 * treatment still going on is open. **Several can be in progress at once**: the
 * client's answer to "should one wait for the one before?" was *"just how it
 * usually goes"*, so nothing here orders or blocks them.
 */
export function TreatmentTab({
  plans,
  open,
  onToggle,
  onChanged,
}: {
  plans: readonly TreatmentPlan[];
  /** A visit was corrected or undone (PP-D5, PP-D6): the plan as it now is. */
  onChanged?: (plan: TreatmentPlan) => void;
  /** What somebody opened or closed by hand, by plan id; the default applies to the rest. */
  open: ReadonlyMap<string, boolean>;
  onToggle: (planId: string, open: boolean) => void;
}): React.JSX.Element {
  // Anything still going on first; finished and closed ones below, as history.
  const going = plans.filter(inProgress);
  const done = plans.filter((plan) => !inProgress(plan));
  const ordered = [...going, ...done];

  const isOpen = (plan: TreatmentPlan): boolean => open.get(plan.id) ?? inProgress(plan);
  // A card opened by hand grows, and can be packed onto the next page: the pager follows it.
  const [follow, setFollow] = useState<Follow | null>(null);

  const pages = useFitPages({
    keys: ordered.map((plan) => plan.id),
    // A closed card is its two lines; an open one adds a 34px row per visit.
    estimate: (index) => {
      const plan = ordered[index];
      if (plan === undefined) return 86;
      return 86 + (isOpen(plan) ? 4 + 34 * plan.parts.length : 0);
    },
    gap: CARD_GAP,
    follow,
  });
  const [start, end] = pages.range;

  if (plans.length === 0) {
    return (
      <p className={styles.well} data-testid="treatment-empty">
        No treatment on this file. A multi-visit procedure booked from the calendar shows here,
        visit by visit.
      </p>
    );
  }

  return (
    <section className={styles.tab} data-testid="treatment-panel">
      <p className={styles.sub} data-testid="treatment-sub">
        {going.length} in progress · {done.length} finished
      </p>
      <div className={styles.area} ref={pages.areaRef}>
        <ol className={styles.cards}>
          {ordered.slice(start, end).map((plan) => (
            <li key={plan.id} ref={pages.measure(plan.id)}>
              <TreatmentCard
                plan={plan}
                open={isOpen(plan)}
                {...(onChanged === undefined ? {} : { onChanged })}
                onToggle={() => {
                  setFollow({ key: plan.id });
                  onToggle(plan.id, !isOpen(plan));
                }}
              />
            </li>
          ))}
        </ol>
      </div>
      <Pager
        page={pages.page}
        count={pages.pages.length}
        onPage={pages.setPage}
        from={start + 1}
        to={end}
        total={ordered.length}
        noun="treatments"
        testId="treatment-pager"
      />
    </section>
  );
}

/** The space between two cards — `.cards` in the CSS draws it. */
const CARD_GAP = 10;

/** Still going on: neither finished nor closed. */
export function inProgress(plan: TreatmentPlan): boolean {
  return plan.completedAt === null && plan.closedAt === null;
}

/**
 * One procedure course or old plan, with its visits.
 *
 * `onToggle` makes it collapsible, which is how the patient file draws it
 * (PF8). Without it the card is always open and has no control at all — the
 * old plan's own address, `/plans/:id`, reads it that way.
 */
export function TreatmentCard({
  plan,
  open = true,
  onToggle,
  onChanged,
}: {
  plan: TreatmentPlan;
  open?: boolean;
  onToggle?: () => void;
  onChanged?: (plan: TreatmentPlan) => void;
}): React.JSX.Element {
  const timezone = useBranchTimezone();
  const currency = useBranchCurrency();
  const can = useCan();
  const [correcting, setCorrecting] = useState<PlanPart | null>(null);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState<string | null>(null);
  /**
   * **Finished work on a course can be corrected** (PP-D5, PP-D6) — an old
   * hand-built plan stays read-only history (PV6), and nobody without
   * `plans.edit` is offered what the server would refuse.
   */
  const correctable = (part: PlanPart): boolean =>
    onChanged !== undefined &&
    plan.procedureCodeId !== null &&
    can("plans.edit") &&
    part.items.some((item) => item.status === "COMPLETED");
  const standing = standingOf(plan, timezone);

  /**
   * **A later visit done first on the calendar waits for this one** (PP-D9):
   * marking the open visit done completes both, in order, and bills them.
   */
  const ahead = inProgress(plan) ? doneAhead(plan) : null;
  const toMark = ahead !== null ? standing.next : null;
  const markDone = (part: PlanPart): void => {
    if (onChanged === undefined) return;
    setMarking(true);
    setMarkError(null);
    completeVisit(plan.id, part.id)
      .then(onChanged)
      .catch((cause: unknown) => {
        setMarkError(
          cause instanceof Error ? cause.message : "That visit could not be marked done.",
        );
      })
      .finally(() => setMarking(false));
  };

  /**
   * **The visit a front desk can book now**: the first unfinished one with no
   * live booking, whether never booked or fallen through — the same rule the
   * booking form's picker offers (PV5). Nothing on a closed or finished plan.
   */
  const bookable = inProgress(plan)
    ? plan.parts.find(
        (part) => part.items.length > 0 && part.completedAt === null && part.booking === null,
      )
    : undefined;

  const title = (
    <>
      <span className={styles.name}>{plan.title ?? "Treatment"}</span>
      {/*
        **Which kind, said in words** — a course moves on with the diary, an
        old plan is history that nothing can change (PV6).
      */}
      <span className={styles.kind} data-testid="treatment-kind">
        {plan.procedureCodeId === null ? "Treatment plan · read only" : "Procedure"}
      </span>
    </>
  );

  return (
    <article
      className={open ? `${styles.card} ${styles.open}` : styles.card}
      data-testid="treatment-card"
      data-plan={plan.id}
      data-open={open}
    >
      <header className={styles.head}>
        {onToggle === undefined ? (
          <div className={styles.headMain}>{title}</div>
        ) : (
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={open}
            onClick={onToggle}
            data-card=""
            data-testid="treatment-toggle"
          >
            <span className={styles.chevron} aria-hidden="true">
              ›
            </span>
            {title}
          </button>
        )}
        {toMark !== null && onChanged !== undefined && can("plans.edit") && (
          <button
            type="button"
            className={styles.book}
            disabled={marking}
            onClick={() => markDone(toMark)}
            data-testid="treatment-mark-done"
          >
            Mark {nameOf(toMark, plan)} done
          </button>
        )}
        {bookable !== undefined && toMark === null && (
          <Link
            to={`/calendar?patient=${plan.patientId}&visit=${plan.id}:${bookable.id}`}
            className={styles.book}
            data-testid="treatment-book"
          >
            Book visit {plan.parts.indexOf(bookable) + 1}
          </Link>
        )}
      </header>

      {/*
        **Where it stands, even closed**: a segment per visit — filled done,
        outlined booked, empty waiting — beside the same fact in words, so the
        colour is never the only carrier.
      */}
      <div className={onToggle === undefined ? styles.sub2 : `${styles.sub2} ${styles.indented}`}>
        <Segments parts={plan.parts} />
        <span data-testid="treatment-standing">
          {standing.doneCount} of {standing.total} done · {standing.summary}
        </span>
        <span className={styles.money}>
          {formatMoney(plan.totalCents, currency)} in all
          {/* Paid needs billing.view; without it the server sends null (QF-D17). */}
          {plan.paidCents !== null && <> · {formatMoney(plan.paidCents, currency)} paid</>}
          {/*
            **Drafted until somebody issues it** (PP-D8): billed by the plan,
            owed by nobody yet, so never said as "invoiced".
          */}
          {plan.draftedCents !== null && plan.draftedCents > 0 && (
            <span data-testid="treatment-drafted">
              {" "}
              · {formatMoney(plan.draftedCents, currency)} drafted
            </span>
          )}
        </span>
      </div>
      {markError !== null && (
        <p className={styles.error} role="alert">
          {markError}
        </p>
      )}

      {open && (
        <ol
          className={onToggle === undefined ? styles.visits : `${styles.visits} ${styles.indented}`}
        >
          {plan.parts.map((part, index) => (
            <li key={part.id} className={styles.visit} data-testid="treatment-visit">
              <span className={styles.number}>{index + 1}</span>
              <span className={styles.visitName}>{capitalise(nameOf(part, plan))}</span>
              <span
                className={
                  part.booking !== null && part.completedAt === null ? styles.booked : styles.state
                }
              >
                {stateOf(part, plan, timezone)}
                {correctable(part) && (
                  <button
                    type="button"
                    className={styles.correct}
                    onClick={() => setCorrecting(part)}
                    data-testid="treatment-correct"
                  >
                    Correct…
                  </button>
                )}
              </span>
              <span className={styles.price}>{formatMoney(priceOf(part), currency)}</span>
            </li>
          ))}
        </ol>
      )}

      {correcting !== null && onChanged !== undefined && (
        <CorrectVisitDialog
          plan={plan}
          part={correcting}
          currency={currency}
          onClose={() => setCorrecting(null)}
          onSaved={onChanged}
        />
      )}
    </article>
  );
}

/** One small bar per visit: done, booked or waiting. */
export function Segments({ parts }: { parts: readonly PlanPart[] }): React.JSX.Element {
  return (
    <span className={styles.segments} aria-hidden="true">
      {parts.map((part) => (
        <i
          key={part.id}
          className={
            part.completedAt !== null
              ? `${styles.segment} ${styles.segmentDone}`
              : part.booking !== null
                ? `${styles.segment} ${styles.segmentBooked}`
                : styles.segment
          }
        />
      ))}
    </span>
  );
}

/** Done, booked, or waiting — in words, never colour alone. */
function stateOf(part: PlanPart, plan: TreatmentPlan, timezone: string): string {
  if (part.completedAt !== null) return `Done ${dayOf(part.completedAt, timezone)}`;
  // Done on the calendar ahead of its turn (PP-D9): waiting, not booked.
  if (part.booking?.status === "COMPLETED" && part.blockedByVisit !== null) {
    const before = plan.parts[part.blockedByVisit - 1];
    return `Done on the calendar · waits for ${before === undefined ? "the visit before" : nameOf(before, plan)}`;
  }
  if (part.booking !== null) return whenOf(part.booking.startsAt, timezone);
  return "Not booked";
}

/** A visit's price: the sum of its steps, as billing reads it. */
function priceOf(part: PlanPart): number {
  return part.items.reduce((sum, item) => sum + (item.priceCents ?? 0), 0);
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
